import { useLocation, Navigate, Outlet } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { selectCurrentToken, logOut, selectNeedsProfileCompletion } from "./redux/auth/authSlice"
import { useEffect, useRef } from 'react'

const decodeJwtExp = (token) => {
  if (!token) return 0
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return (payload.exp || 0) * 1000
  } catch {
    return 0
  }
}

const RequireAuth = () => {
  const dispatch = useDispatch()
  const token = useSelector(selectCurrentToken)
  const needsProfile = useSelector(selectNeedsProfileCompletion)
  const location = useLocation()
  const logoutRef = useRef(false)

  // Immediate expiration check & schedule
  useEffect(() => {
    if (!token) return
    const expMs = decodeJwtExp(token)
    if (!expMs || expMs < Date.now()) {
      if (!logoutRef.current) {
        logoutRef.current = true
        dispatch(logOut())
      }
      return
    }
    const timeoutId = setTimeout(() => {
      if (!logoutRef.current) {
        logoutRef.current = true
        dispatch(logOut())
      }
    }, Math.max(0, expMs - Date.now()))
    return () => clearTimeout(timeoutId)
  }, [token, dispatch])

  // Cross-tab + focus revalidation
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'authToken' && !e.newValue) {
        dispatch(logOut())
      }
    }
    const revalidate = () => {
      if (!token) return
      const expMs = decodeJwtExp(token)
      if (expMs < Date.now()) {
        dispatch(logOut())
      }
    }
    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', revalidate)
    document.addEventListener('visibilitychange', revalidate)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', revalidate)
      document.removeEventListener('visibilitychange', revalidate)
    }
  }, [token, dispatch])

  // Optional: keep a mirror in localStorage to sync tabs
  useEffect(() => {
    if (token) localStorage.setItem('authToken', token)
    else localStorage.removeItem('authToken')
  }, [token])

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  if (needsProfile) {
    return <Navigate to="/profile/complete" replace />
  }
  return <Outlet />
}
export default RequireAuth
export const useIsAuthenticated = () => {
  const token = useSelector(selectCurrentToken)
  return Boolean(token);
}
